import { Button } from '@app/components/various/Button';
import { ShoppingBag } from '@app/components/various/Icons';
import { Tooltip } from '@app/components/various/Tooltip';
import { addAllProductsToOrderRequest, getIsAnyProductLoading, getIsSavingAnything, getOrderProductIds } from '@app/ducks';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { useConfirmationGuard } from '@app/utils/hooks';
import { ProductFilters } from '@typings';
import { without } from 'ramda';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

interface Props {
  showAddAll: boolean;
  itemsCount: number;
  onAddAll?: () => void;
  filters: ProductFilters;
  allProductsIds?: string[];
}

export const FiltersSummary = ({ showAddAll, itemsCount, onAddAll, filters, allProductsIds = [] }: Props) => {
  const { t } = useTranslation(['common', 'products', 'confirmationConfig']);
  const isSavingAnything = useSelector(getIsSavingAnything);
  const isLoadingAnything = useSelector(getIsAnyProductLoading);
  const orderProductIds = useSelector(getOrderProductIds);

  const dispatch = useDispatch();
  const confirmationGuard = useConfirmationGuard();
  const { trackEvent } = useAmplitude();

  const isAddAllDisabled = isSavingAnything || isLoadingAnything;

  const hasItemsToAdd = itemsCount > 0;

  const handleAddProducts = React.useCallback(() => {
    trackEvent({ name: 'listing.button.addall.click' });

    dispatch(
      addAllProductsToOrderRequest({
        filters,
        products: without(orderProductIds, allProductsIds),
      }),
    );
  }, [dispatch, filters, orderProductIds, allProductsIds, trackEvent]);

  const addAllProducts = () => {
    onAddAll?.();
    confirmationGuard(() => ({
      okText: t('common:ok'),
      okType: 'info',
      onOk: handleAddProducts,
      ...t('confirmationConfig:add_all_products', { returnObjects: true }),
    }))();
  };

  return (
    <div className="flex h-5 items-center gap-1">
      <div className="text-neutral-60 heading-4">{t('products:product_count', { count: itemsCount })}</div>
      {hasItemsToAdd && showAddAll && (
        <Tooltip content={t('products:add_all_products')}>
          <Button
            className="rounded-small border border-solid border-neutral-90"
            variant="text"
            color="secondary"
            disabled={isAddAllDisabled}
            onClick={addAllProducts}
            icon={ShoppingBag}
            aria-label={t('products:add_all_products')}
          />
        </Tooltip>
      )}
    </div>
  );
};
